import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FaPlus, 
  FaTrophy, 
  FaMoneyBillWave, 
  FaUsers, 
  FaDoorOpen, 
  FaKey, 
  FaTag,
  FaCalendarAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner
} from 'react-icons/fa';
import './CreateMatch.css';

const CreateMatch = ({ onCreated }) => {
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [matchData, setMatchData] = useState({
    title: '',
    prize: '',
    entry_fee: '',
    max_players: '',
    room_id: '',
    room_password: '',
    match_tag_id: '',
    text_time: ''
  });

  const [validationErrors, setValidationErrors] = useState({
    title: '',
    prize: '',
    entry_fee: '',
    max_players: '',
    text_time: ''
  });

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const res = await axios.get('https://backend.aubattle.com/api/matches/tags');
        setTags(res.data);
      } catch (err) {
        console.error("Failed to fetch tags", err);
      }
    };
    fetchTags();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMatchData({ ...matchData, [name]: value });
    
    // Clear validation error when user types
    if (validationErrors[name]) {
      setValidationErrors({ ...validationErrors, [name]: '' });
    }
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    if (!matchData.title.trim()) {
      errors.title = 'Title is required';
      isValid = false;
    }

    if (!matchData.prize || isNaN(matchData.prize)) {
      errors.prize = 'Valid prize amount is required';
      isValid = false;
    }

    if (!matchData.entry_fee || isNaN(matchData.entry_fee)) {
      errors.entry_fee = 'Valid entry fee is required';
      isValid = false;
    }

    if (!matchData.max_players || isNaN(matchData.max_players)) {
      errors.max_players = 'Valid player count is required';
      isValid = false;
    }

    if (!matchData.text_time) {
      errors.text_time = 'Match time is required';
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setIsLoading(true);
      setError(null);
      
      await axios.post('https://backend.aubattle.com/api/matches', matchData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setSuccess('Match created successfully!');
      setMatchData({
        title: '',
        prize: '',
        entry_fee: '',
        max_players: '',
        room_id: '',
        room_password: '',
        match_tag_id: '',
        text_time: ''
      });
      
      if (onCreated) onCreated();
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      console.error("Failed to create match", err);
      setError(err.response?.data?.message || "Failed to create match. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="create-match-container">
      <div className="form-header">
        <h2><FaPlus /> Create New Match</h2>
        <p>Fill in the details to create a new tournament match</p>
      </div>

      {error && (
        <div className="alert error">
          <FaTimesCircle /> {error}
        </div>
      )}

      {success && (
        <div className="alert success">
          <FaCheckCircle /> {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="match-form">
        <div className="form-group">
          <label htmlFor="title">
            <FaTrophy /> Match Title
          </label>
          <input
            id="title"
            name="title"
            type="text"
            placeholder="Enter match title"
            value={matchData.title}
            onChange={handleChange}
            className={validationErrors.title ? 'error' : ''}
          />
          {validationErrors.title && (
            <span className="error-message">{validationErrors.title}</span>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="prize">
              <FaTrophy /> Prize Pool ($)
            </label>
            <input
              id="prize"
              name="prize"
              type="number"
              placeholder="100"
              value={matchData.prize}
              onChange={handleChange}
              className={validationErrors.prize ? 'error' : ''}
            />
            {validationErrors.prize && (
              <span className="error-message">{validationErrors.prize}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="entry_fee">
              <FaMoneyBillWave /> Entry Fee ($)
            </label>
            <input
              id="entry_fee"
              name="entry_fee"
              type="number"
              placeholder="10"
              value={matchData.entry_fee}
              onChange={handleChange}
              className={validationErrors.entry_fee ? 'error' : ''}
            />
            {validationErrors.entry_fee && (
              <span className="error-message">{validationErrors.entry_fee}</span>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="max_players">
              <FaUsers /> Max Players
            </label>
            <input
              id="max_players"
              name="max_players"
              type="number"
              placeholder="10"
              value={matchData.max_players}
              onChange={handleChange}
              className={validationErrors.max_players ? 'error' : ''}
            />
            {validationErrors.max_players && (
              <span className="error-message">{validationErrors.max_players}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="text_time">
              <FaCalendarAlt /> Match Time
            </label>
            <input
              id="text_time"
              name="text_time"
              type="text"
              placeholder="e.g., Today at 8:00 PM"
              value={matchData.text_time}
              onChange={handleChange}
              className={validationErrors.text_time ? 'error' : ''}
            />
            {validationErrors.text_time && (
              <span className="error-message">{validationErrors.text_time}</span>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="room_id">
              <FaDoorOpen /> Room ID (Optional)
            </label>
            <input
              id="room_id"
              name="room_id"
              type="text"
              placeholder="Enter room ID"
              value={matchData.room_id}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="room_password">
              <FaKey /> Room Password (Optional)
            </label>
            <input
              id="room_password"
              name="room_password"
              type="text"
              placeholder="Enter password"
              value={matchData.room_password}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="match_tag_id">
            <FaTag /> Match Tag
          </label>
          <select
            id="match_tag_id"
            name="match_tag_id"
            value={matchData.match_tag_id}
            onChange={handleChange}
          >
            <option value="">Select a tag (Optional)</option>
            {tags.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
              </option>
            ))}
          </select>
        </div>

        <button 
          type="submit" 
          className="submit-btn"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <FaSpinner className="spin" /> Creating...
            </>
          ) : (
            <>
              <FaPlus /> Create Match
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default CreateMatch;